"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";

import { richTextComponent } from "@/sanity/sanityPortableText";
import { cn } from "@/lib/utils";

import { PipeChip } from "@/components/global/sections/home/_shared";
import { FadeIn } from "@/components/global/utils/FadeIn";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/shadcn/ui/carousel";

import type { IRankTier } from "@/sanity/pages/PageLeaderboardTemplate";

import { RankAvatar, RewardsDivider, RewardRow, RANK_ART } from "./RankAvatar";
import { RankPointsCount, RankRevealScope } from "./RankReveal";

import checkmarkIcon from "@/public/leaderboard/ranks/checkmark.svg";
import lockedIcon from "@/public/leaderboard/ranks/locked.svg";

const PILL = "flex min-h-[2.5rem] shrink-0 items-center gap-[0.5rem] rounded-[0.5rem] px-[0.5rem]";
const PILL_LABEL =
  "translate-y-[0.029em] whitespace-nowrap font-geist text-[1.75rem] font-medium uppercase leading-none tracking-[0.035rem]";

type StatusLabels = { achieved?: string; inProgress?: string; locked?: string };

function StatusPill({ status, labels }: { status?: IRankTier["status"]; labels: StatusLabels }) {
  if (status === "achieved") {
    return (
      <div className={cn(PILL, "border border-[rgba(70,210,133,0.3)] bg-[rgba(70,210,133,0.15)]")}>
        <Image src={checkmarkIcon} alt="" className="h-[2rem] w-[2rem] shrink-0" />
        <span className={cn(PILL_LABEL, "text-[#46d285]")}>{labels.achieved}</span>
      </div>
    );
  }
  if (status === "inProgress") {
    return (
      <div className={cn(PILL, "bg-[#fffae7]")}>
        <span className={cn(PILL_LABEL, "text-black opacity-80")}>{labels.inProgress}</span>
      </div>
    );
  }
  return (
    <div className={cn(PILL, "border border-white/30 bg-white/10")}>
      <Image src={lockedIcon} alt="" className="h-[2rem] w-[2rem] shrink-0" />
      <span className={cn(PILL_LABEL, "text-white opacity-70")}>{labels.locked}</span>
    </div>
  );
}

function TierCard({
  tier,
  index,
  selected,
  onHover,
  statusLabels,
  rewardsLabel,
}: {
  tier: IRankTier;
  index: number;
  selected: boolean;
  onHover: (on: boolean) => void;
  statusLabels: StatusLabels;
  rewardsLabel?: string;
}) {
  const art = RANK_ART[index] ?? RANK_ART[RANK_ART.length - 1];
  const inProgress = tier.status === "inProgress";
  const locked = tier.status === "locked";

  return (
    <RankRevealScope
      onMouseEnter={() => {
        if (window.matchMedia("(hover: hover)").matches) onHover(true);
      }}
      onMouseLeave={() => onHover(false)}
      className={cn(
        "relative flex h-full w-full flex-col items-center gap-[3rem] overflow-clip rounded-[1rem] border bg-white/[0.05] px-[1.5rem] pb-[4.5rem] pt-[2rem] backdrop-blur-[4.75rem]",
        "transition-[opacity,box-shadow,border-color] duration-500 ease-out",
        selected
          ? "z-10 shadow-[inset_0_0_1.5rem_0_rgba(255,250,231,0.24),inset_0.5rem_10rem_10.5rem_0_rgba(255,250,231,0.1)]"
          : "shadow-none",
        !selected && tier.status === "achieved" && "opacity-80",
        !selected && locked && "opacity-[0.65]",
      )}
      style={{ borderColor: selected ? "rgba(255,253,244,0.28)" : "rgba(255,255,255,0.1)" }}
    >
      {locked && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(120deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 10px)",
          }}
        />
      )}

      <div className="relative flex w-full shrink-0 flex-col items-center gap-[1rem]">
        {tier.name && (
          <p className="m-0 w-full truncate text-center font-geist text-[2.25rem] font-medium leading-none text-white">
            {tier.name}
          </p>
        )}
        <StatusPill status={tier.status} labels={statusLabels} />
      </div>

      <RankAvatar
        art={art}
        portrait={tier.avatar}
        selected={selected}
        seed={index}
        className="w-[min(27.375rem,78%)] shrink-0"
      />

      <p className="relative m-0 text-center font-geist text-[3rem] font-medium leading-none text-white">
        {inProgress && tier.pointsCurrent != null ? (
          <>
            <RankPointsCount value={tier.pointsCurrent} />{" "}
            <span className="text-white/30">/ {tier.pointsTarget} pts</span>
          </>
        ) : (
          <>
            {tier.pointsTarget} <span className="text-white/25">pts</span>
          </>
        )}
      </p>

      <RewardsDivider ornate={inProgress} label={rewardsLabel} />

      {tier.rewards && tier.rewards.length > 0 && (
        <div className="relative flex min-w-0 flex-col items-start gap-[1.25rem] opacity-70">
          {tier.rewards.map((reward, i) => (
            <RewardRow key={i} icon={reward.icon} label={reward.label} value={reward.value} />
          ))}
        </div>
      )}
    </RankRevealScope>
  );
}

function useWheelNav(
  api: CarouselApi | undefined,
  ref: React.RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !api) return;

    let last = 0;
    const onWheel = (e: WheelEvent) => {
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!delta) return;

      const forward = delta > 0;
      if (forward ? !api.canScrollNext() : !api.canScrollPrev()) return;
      e.preventDefault();

      if (e.timeStamp - last < 320) return;
      last = e.timeStamp;
      if (forward) api.scrollNext();
      else api.scrollPrev();
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [api, ref]);
}

function NavArrow({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex h-[6rem] w-[6rem] shrink-0 items-center justify-center rounded-full bg-white text-black transition-opacity duration-200",
        disabled ? "opacity-25" : "opacity-100 hover:opacity-80",
      )}
    >
      {children}
    </button>
  );
}

export default function RankTiers({
  chip,
  title,
  subtitle,
  items,
  comingSoonTitle,
  comingSoonText,
  rewardsLabel,
  statusAchieved,
  statusInProgress,
  statusLocked,
}: {
  chip?: string;
  title?: PortableTextBlock[];
  subtitle?: string;
  items?: IRankTier[];
  comingSoonTitle?: string;
  comingSoonText?: string;
  rewardsLabel?: string;
  statusAchieved?: string;
  statusInProgress?: string;
  statusLocked?: string;
}) {
  const statusLabels: StatusLabels = {
    achieved: statusAchieved,
    inProgress: statusInProgress,
    locked: statusLocked,
  };
  const tiers = (items ?? []).filter(Boolean);

  const featured = Math.max(
    0,
    tiers.findIndex((t) => t.status === "inProgress"),
  );

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const release = useRef<ReturnType<typeof setTimeout>>();
  const hover = (i: number, on: boolean) => {
    clearTimeout(release.current);
    if (on) {
      setHoveredIndex(i);
      return;
    }
    release.current = setTimeout(() => setHoveredIndex(null), 80);
  };
  useEffect(() => () => clearTimeout(release.current), []);

  const [api, setApi] = useState<CarouselApi>();
  const [snaps, setSnaps] = useState<number[]>([]);
  const [selected, setSelected] = useState(featured);
  const [scrollable, setScrollable] = useState(false);
  const wheelRef = useRef<HTMLDivElement>(null);

  // Below `lg` the carousel is the only way through the ranks and there is no
  // hover to light the card you land on, so the highlight would sit on the
  // in-progress tier however far you swiped — the swipe reads as dead. There,
  // the card on the active dot takes it. On desktop the whole row fits (embla
  // reports nothing to scroll), so hover drives it and in-progress stays the
  // resting state.
  const selectedIndex = hoveredIndex ?? (scrollable ? selected : featured);
  // The Coming Soon card sits at `tiers.length` in the carousel, so it is just
  // another index: no tier matches it, the highlight leaves them, and on
  // desktop the 80ms release below hands it back to the featured tier on the
  // way out. It takes the highlight, not the reveal — there is no rank art.
  const comingSoonActive = selectedIndex === tiers.length;

  useWheelNav(api, wheelRef);

  useEffect(() => {
    if (!api) return;
    const sync = () => {
      setSnaps(api.scrollSnapList());
      setSelected(api.selectedScrollSnap());
      setScrollable(api.canScrollNext() || api.canScrollPrev());
    };
    sync();
    api.on("select", sync);
    api.on("reInit", sync);
    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  return (
    <section className="relative w-full overflow-hidden bg-black py-[10rem] lg:py-[17.5rem]">
      <div className="mx-auto flex w-[min(149.25rem,100%)] flex-col items-center gap-[5rem] px-[3rem]">
        <div className="flex w-full flex-col items-center justify-center gap-[3.375rem] text-center lg:gap-[3.125rem]">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip className="m-0">{chip}</PipeChip>
            </FadeIn>
          )}
          {(title || subtitle) && (
            <div className="flex w-full flex-col items-center gap-[2rem]">
              {title && (
                <FadeIn
                  delay={0.1}
                  className="richtext richtext-center w-[min(72.25rem,100%)] [text-wrap:balance] prose-p:m-0 prose-p:font-news prose-p:text-[4.5rem] prose-p:font-normal prose-p:leading-[1.1] prose-p:tracking-[-0.25rem] prose-p:text-white/90 lg:prose-p:text-[6rem] prose-headings:m-0 prose-headings:font-news prose-headings:text-[4.5rem] prose-headings:font-normal prose-headings:leading-[1.1] prose-headings:tracking-[-0.25rem] prose-headings:text-white/90 lg:prose-headings:text-[6rem] prose-em:font-news prose-em:italic"
                >
                  <PortableText value={title} components={richTextComponent} />
                </FadeIn>
              )}
              {subtitle && (
                <FadeIn delay={0.15} className="w-[min(62rem,100%)]">
                  <p className="m-0 font-geist text-[2.25rem] leading-[1.5] text-white/60">
                    {subtitle}
                  </p>
                </FadeIn>
              )}
            </div>
          )}
        </div>
      </div>

      {(tiers.length > 0 || comingSoonTitle) && (
        <div ref={wheelRef} className="relative mt-[5rem] w-full">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              dragFree: true,
              breakpoints: {
                "(max-width: 992px)": {
                  align: "center",
                  startIndex: featured,
                  dragFree: false,
                },
              },
            }}
            className="w-full"
          >
            {/* The row is 210rem wide, so the header's 149.25rem gutter centred the
                wrong box and pushed it right. Card and gap are both rem, so at lg
                the row is always ~88% of the viewport — it fits, and centring is
                safe. Below lg it scrolls, opening on the in-progress rank. */}
            <CarouselContent className="ml-0 items-stretch gap-[2rem] px-[3rem] lg:justify-center">
              {tiers.map((tier, i) => (
                <CarouselItem key={i} className="basis-[min(40.4rem,80%)] pl-0">
                  <FadeIn delay={0.15 + i * 0.09} className="h-full">
                    <TierCard
                      tier={tier}
                      index={i}
                      selected={i === selectedIndex}
                      onHover={(on) => hover(i, on)}
                      statusLabels={statusLabels}
                      rewardsLabel={rewardsLabel}
                    />
                  </FadeIn>
                </CarouselItem>
              ))}
              <CarouselItem className="basis-[min(40.4rem,80%)] pl-0">
                <FadeIn delay={0.15 + tiers.length * 0.09} className="h-full">
                <div
                  onMouseEnter={() => {
                    if (window.matchMedia("(hover: hover)").matches) hover(tiers.length, true);
                  }}
                  onMouseLeave={() => hover(tiers.length, false)}
                  className={cn(
                    "relative flex h-full w-full flex-col items-center justify-center gap-[1rem] overflow-clip rounded-[1rem] border px-[3rem] py-[6rem] text-center",
                    "transition-[opacity,box-shadow,border-color] duration-500 ease-out",
                    comingSoonActive
                      ? "z-10 shadow-[inset_0_0_1.5rem_0_rgba(255,250,231,0.24),inset_0.5rem_10rem_10.5rem_0_rgba(255,250,231,0.1)]"
                      : "opacity-[0.65] shadow-none",
                  )}
                  style={{
                    borderColor: comingSoonActive
                      ? "rgba(255,253,244,0.28)"
                      : "rgba(255,255,255,0.1)",
                  }}>
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 opacity-[0.06]"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(120deg, rgba(255,255,255,0.6) 0 1px, transparent 1px 10px)",
                    }}
                  />
                  {comingSoonTitle && (
                    <p className="relative m-0 font-geist text-[3rem] font-medium leading-none text-white opacity-70">
                      {comingSoonTitle}
                    </p>
                  )}
                  {comingSoonText && (
                    <p className="relative m-0 w-[min(34rem,100%)] font-geist text-[2rem] font-medium leading-[1.5] text-white opacity-[0.35]">
                      {comingSoonText}
                    </p>
                  )}
                </div>
                </FadeIn>
              </CarouselItem>
            </CarouselContent>
          </Carousel>

          {scrollable && snaps.length > 1 && (
            <div className="mt-[4rem] flex items-center justify-between px-[5rem]">
              <NavArrow
                label="Previous rank"
                disabled={!api?.canScrollPrev()}
                onClick={() => api?.scrollPrev()}
              >
                <ChevronLeft className="h-[3rem] w-[3rem]" strokeWidth={1.5} />
              </NavArrow>

              <div className="flex items-center gap-[1.5rem]">
                {snaps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to rank ${i + 1}`}
                    aria-current={i === selected}
                    onClick={() => api?.scrollTo(i)}
                    className={cn(
                      "h-[1rem] rounded-full transition-all duration-300",
                      i === selected ? "w-[4rem] bg-white" : "w-[1rem] bg-white/40",
                    )}
                  />
                ))}
              </div>

              <NavArrow
                label="Next rank"
                disabled={!api?.canScrollNext()}
                onClick={() => api?.scrollNext()}
              >
                <ChevronRight className="h-[3rem] w-[3rem]" strokeWidth={1.5} />
              </NavArrow>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
